import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';

const Login = () => {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex?.test(email);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.email?.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!validateEmail(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.password?.trim()) {
      newErrors.password = 'Password is required';
    } else if (formData?.password?.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    const { data, error } = await signIn(formData?.email, formData?.password);

    if (error) {
      setErrors({
        email: error?.message || 'Invalid email or password',
        password: 'Invalid email or password'
      });
      setIsLoading(false);
    } else {
      navigate('/providers-dashboard');
    }
  };

  const handleForgotPassword = () => {
    console.log('Forgot password clicked');
  };

  const handleRequestAccess = () => {
    navigate('/invite-signup');
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4 bg-primary">
            <Icon name="/assets/images/Untitled-1768978080610.jpeg" size={36} />
          </div>
          <h1 className="text-3xl md:text-4xl font-semibold text-foreground mb-2">Videra</h1>
          <p className="text-sm md:text-base text-muted-foreground font-caption">Adaptable Tissue Marker</p>
        </div>

        <div className="bg-card border border-border rounded-2xl shadow-lg p-6 md:p-8">
          <div className="mb-6">
            <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-2">Welcome Back</h2>
            <p className="text-sm text-muted-foreground font-caption">Sign in to access your provider portal</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <Input
              type="email"
              name="email"
              label="Email Address"
              placeholder="Enter your email"
              value={formData?.email}
              onChange={handleInputChange}
              error={errors?.email}
              required
              disabled={isLoading}
            />

            <Input
              type="password"
              name="password"
              label="Password"
              placeholder="Enter your password"
              value={formData?.password}
              onChange={handleInputChange}
              error={errors?.password}
              required
              disabled={isLoading}
            />

            <div className="flex items-center justify-end">
              <button
                type="button"
                onClick={handleForgotPassword}
                className="text-sm transition-colors duration-250 font-medium"
                style={{ color: 'var(--corvid-background-color, rgba(var(--bg, var(--color_17, 37, 99, 235)), var(--alpha-bg, 1)))' }}
                disabled={isLoading}
              >
                Forgot Password?
              </button>
            </div>

            <Button
              type="submit"
              variant="default"
              fullWidth
              loading={isLoading}
              disabled={isLoading}
              iconName="LogIn"
              iconPosition="right"
              className="mt-6"
            >
              Sign In
            </Button>
          </form>

          <div className="mt-6 pt-6 border-t border-border">
            <div className="bg-muted rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Icon name="Info" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-foreground mb-1">Invite-Only Access</p>
                  <p className="text-xs text-muted-foreground font-caption mb-3">
                    This portal is exclusively for authorized medical providers and practice marketing teams.
                  </p>
                  <button
                    type="button"
                    onClick={handleRequestAccess}
                    className="text-sm transition-colors duration-250 font-medium"
                    style={{ color: 'var(--corvid-background-color, rgba(var(--bg, var(--color_17, 37, 99, 235)), var(--alpha-bg, 1)))' }}
                    disabled={isLoading}
                  >
                    Request Access →
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 text-center">
          <p className="text-xs text-muted-foreground font-caption">
            © {new Date()?.getFullYear()} Videra Surgical. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;